import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { useSaveVendorCardMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

export const VendorPayPalPayment = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [formData, setFormData] = useState({
    cardNumber: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
  });



  const [saveCard, { loading }] = useSaveVendorCardMutation({
    onCompleted: (data) => {
      if (data?.saveVendorCard?.success) {
        notify({
          status: "success",
          text: data.saveVendorCard.message || "Card saved successfully.",
        });
        // Reset form
        setFormData({
          cardNumber: "",
          expiryMonth: "",
          expiryYear: "",
          cvv: "",
        });
      } else {
        notify({
          status: "error",
          text: data?.saveVendorCard?.message || "Failed to save card. Please try again.",
        });
      }
    },
    onError: (error) => {
      notify({
        status: "error",
        text: error.message || "Failed to save card. Please try again.",
      });
    },
  });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.cardNumber.length < 13 || formData.cardNumber.length > 19) {
      notify({
        status: "error",
        text: "Please enter a valid card number",
      });

      return;
    }

    if (!/^(0[1-9]|1[0-2])$/.test(formData.expiryMonth)) {
      notify({
        status: "error",
        text: "Please enter a valid expiry month (01-12)",
      });

      return;
    }

    if (!/^\d{4}$/.test(formData.expiryYear)) {
      notify({
        status: "error",
        text: "Please enter a valid expiry year (YYYY)",
      });

      return;
    }

    if (!/^\d{3,4}$/.test(formData.cvv)) {
      notify({
        status: "error",
        text: "Please enter a valid CVV (3-4 digits)",
      });

      return;
    }

    try {
      await saveCard({
        variables: {
          input: {
            cardNumber: formData.cardNumber.replace(/\s/g, ""),
            expiryMonth: formData.expiryMonth,
            expiryYear: formData.expiryYear,
            cvv: formData.cvv,
          },
        },
      });
    } catch (error) {
      // Error handled in onError callback
      console.error("Card save error:", error);
    }
  };

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage({ id: '4vs06P', defaultMessage: "Update Card" })} style={{ fontWeight: 'bold' }} />

      <Box paddingX={9} paddingY={6}>
        {/* Header Section with Icon and Description */}
        <Box marginBottom={6}>
          <Box display="flex" alignItems="center" gap={3} marginBottom={3}>
            <Box
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 4H3C1.89543 4 1 4.89543 1 6V18C1 19.1046 1.89543 20 3 20H21C22.1046 20 23 19.1046 23 18V6C23 4.89543 22.1046 4 21 4Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M1 10H23" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Box>
            <Box>
              <Text size={5} fontWeight="bold" style={{ display: 'block', marginBottom: '4px' }}>
                {intl.formatMessage({ id: 'updateCardTitle', defaultMessage: "Update Card" })}
              </Text>
              <Text size={3} color="default2">
                {intl.formatMessage({ id: 'updateCardSubtitle', defaultMessage: "Securely save your payment method" })}
              </Text>
            </Box>
          </Box>
        </Box>

        <DashboardCard>
          <Box padding={8}>
            <form onSubmit={handleSubmit}>
              <Box display="flex" flexDirection="column" style={{ maxWidth: '650px', gap: '24px' }}>

                {/* Card Number */}
                <Box>
                  <label
                    htmlFor="cardNumber"
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 500
                    }}
                  >
                    {intl.formatMessage({ id: 'WTocjo', defaultMessage: "Card Number" })}
                  </label>
                  <Input
                    id="cardNumber"
                    type="text"
                    value={formData.cardNumber}
                    onChange={handleChange("cardNumber")}
                    placeholder="1234 5678 9012 3456"
                    disabled={loading}
                    required
                    maxLength={19}
                  />
                  <Text size={2} color="default2" style={{ display: 'block', marginTop: '6px', fontStyle: 'italic' }}>
                    {intl.formatMessage({ id: 'aY3zYq', defaultMessage: "Enter 13-19 digit card number" })}
                  </Text>
                </Box>

                {/* Expiry and CVV Row */}
                <Box display="flex" style={{ gap: '20px' }}>
                  <Box style={{ flex: 1 }}>
                    <label
                      htmlFor="expiryMonth"
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 500
                      }}
                    >
                      {intl.formatMessage({ id: 'S8iRea', defaultMessage: "Expiry Month" })}
                    </label>
                    <Input
                      id="expiryMonth"
                      type="text"
                      value={formData.expiryMonth}
                      onChange={handleChange("expiryMonth")}
                      placeholder="MM"
                      disabled={loading}
                      required
                      maxLength={2}
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <label
                      htmlFor="expiryYear"
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 500
                      }}
                    >
                      {intl.formatMessage({ id: 'ncLDK1', defaultMessage: "Expiry Year" })}
                    </label>
                    <Input
                      id="expiryYear"
                      type="text"
                      value={formData.expiryYear}
                      onChange={handleChange("expiryYear")}
                      placeholder="YYYY"
                      disabled={loading}
                      required
                      maxLength={4}
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <label
                      htmlFor="cvv"
                      style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 500
                      }}
                    >
                      {intl.formatMessage({ id: 'qkh65Y', defaultMessage: "CVV" })}
                    </label>
                    <Input
                      id="cvv"
                      type="password"
                      value={formData.cvv}
                      onChange={handleChange("cvv")}
                      placeholder="123"
                      disabled={loading}
                      required
                      maxLength={4}
                    />
                  </Box>
                </Box>

                {/* Security Notice */}
                <Box
                  padding={3}
                  style={{
                    backgroundColor: '#F7FAFC',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0'
                  }}
                >
                  <Box display="flex" gap={2} alignItems="center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="#48BB78" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <Text size={2} style={{ color: '#2D3748' }}>
                      {intl.formatMessage({ id: 'secureEncryption', defaultMessage: "Your payment information is encrypted and secure" })}
                    </Text>
                  </Box>
                </Box>

                {/* Submit Button */}
                <Box marginTop={2}>
                  <Button
                    type="submit"
                    disabled={loading}
                    variant="primary"
                    style={{
                      width: '100%',
                      height: '48px',
                      borderRadius: '8px',
                      boxShadow: loading ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }}
                  >
                    {loading
                      ? intl.formatMessage({ id: 'savingCard', defaultMessage: "Saving Card..." })
                      : intl.formatMessage({ id: 'saveCard', defaultMessage: "Save Card" })
                    }
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        </DashboardCard>
      </Box>
    </ListPageLayout>
  );
};

export default VendorPayPalPayment;
